import { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'

import { connect } from 'react-redux'

import { fetchListBanners } from 'actions/ContentActions'
import { resetSessionId } from 'actions/CruiseActions'

import { BANNER_LOCATION_CRUISES } from 'constants/bannerLocations'
import { getCruiseSearchStateFromParams } from 'lib/cruises/cruiseUtils'

import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'

import { useGlobalCruisesSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalCruisesSearch'
import { useAppDispatch } from 'hooks/reduxHooks'
import useQueryParams from 'hooks/useQueryParams'

import Sheet from 'components/utils/Sheet'
import Leaderboard from 'components/Common/Banner/Leaderboard'
import Group from 'components/utils/Group'
import SupportTile from 'components/Common/SupportTile/SupportTile'
import LayoutContainer from 'components/Common/LayoutContainer'

import CruiseHeadData from '../Common/CruiseHeadData'
import CruisePageSearchHero from './CruisePageSearchHero'
import CruisePageBrowseByPorts from './CruisePageBrowseByPorts'
import CruisePageUSPsSection from './CruisePageUSPsSection'
import CruisePageContentSection from './CruisePageContentSection'
import useCruisePageContentOrder from './useCruisePageContentOrder'

interface Props {
  banners?: Array<App.ListBanner>
}

function CruisesPage(props: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const queryParams = useQueryParams()

  useEffect(() => {
    dispatch(fetchListBanners(BANNER_LOCATION_CRUISES))
  }, [dispatch])

  const banners = props.banners || []
  const carouselOrder = useCruisePageContentOrder()

  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const initialSearchState = useMemo(() => getCruiseSearchStateFromParams(queryParams), [])
  const { globalCruisesSearchDispatch, globalCruisesSearchState } = useGlobalCruisesSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    ...initialSearchState,
  })

  useEffect(() => {
    dispatch(resetSessionId())
  }, [dispatch, globalCruisesSearchDispatch])

  return (
    <AnalyticsPageContext.Provider value={AnalyticsPage.verticalPage}>
      <CruiseHeadData />

      <GlobalSearchContextProvider
        state={globalCruisesSearchState}
        dispatch={globalCruisesSearchDispatch}
      >
        <CruisePageSearchHero />

        <Sheet padding="24 0">
          <Group direction="vertical" gap={56}>
            {carouselOrder.slice(0, 9).map(contentName => (
              <CruisePageContentSection key={contentName} contentName={contentName} />
            ))}

            {banners && <Leaderboard banners={banners} />}
            <CruisePageUSPsSection />

            {carouselOrder.slice(9, 12).map(contentName => (
              <CruisePageContentSection key={contentName} contentName={contentName} />
            ))}

            <LayoutContainer>
              <SupportTile description={intl.formatMessage({
                id: 'pFOvv6ZwcPPByHe9mCHO',
                defaultMessage: 'Call our dedicated customer service team anytime.',
                description: 'Support tile description on cruises page in the cruise section',
              })}
              />
            </LayoutContainer>

            {carouselOrder.slice(12).map(contentName => (
              <CruisePageContentSection key={contentName} contentName={contentName} />
            ))}

            <CruisePageBrowseByPorts />
          </Group>
        </Sheet>
      </GlobalSearchContextProvider>
    </AnalyticsPageContext.Provider>
  )
}

function mapStateToProps(state: App.State) {
  return {
    banners: state.content.listBanners.cruises,
  }
}

export default connect(mapStateToProps)(CruisesPage)
